import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
export function ApiHostedCheckoutPage() {
  return <DocPageLayout title="Hosted Checkout API" description="Request to initiate a Hosted Checkout interaction." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Hosted Checkout'
  }]} sections={[{
    id: 'create-checkout',
    title: 'Create Checkout Session'
  }, {
    id: 'retrieve-result',
    title: 'Retrieve Result'
  }]} data-id="element-550">
      <h2 id="create-checkout" className="text-2xl font-bold text-gray-900 mt-6 mb-4 border-b border-gray-200 pb-2" data-id="element-551">
        Create Checkout Session
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-552">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 mr-2" data-id="element-553">
          POST
        </span>
        <span className="text-gray-600" data-id="element-554">/merchant/</span>
        <span className="text-blue-600" data-id="element-555">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-556">/session</span>
      </div>
      <CodeBlock language="JSON" title="Request" code={`{
  "apiOperation": "CREATE_CHECKOUT_SESSION",
  "interaction": {
    "operation": "PURCHASE",
    "returnUrl": "https://your-site.com/receipt",
    "cancelUrl": "https://your-site.com/cancel",
    "merchant": {
      "name": "Your Store",
      "logo": "https://your-site.com/logo.png"
    },
    "displayControl": {
      "billingAddress": "HIDE",
      "shipping": "HIDE"
    }
  },
  "order": {
    "id": "ORDER-HC-001",
    "amount": "150.00",
    "currency": "USD",
    "description": "Premium subscription"
  }
}`} data-id="element-557" />
      <CodeBlock language="JSON" title="Response" code={`{
  "merchant": "TEST12345678",
  "result": "SUCCESS",
  "session": {
    "id": "SESSION0002abcdef1234567890",
    "updateStatus": "NO_UPDATE",
    "version": "v1"
  },
  "successIndicator": "abc123def456"
}`} data-id="element-558" />

      <h2 id="retrieve-result" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-559">
        Retrieve Checkout Session
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-560">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 mr-2" data-id="element-561">
          GET
        </span>
        <span className="text-gray-600" data-id="element-562">/merchant/</span>
        <span className="text-blue-600" data-id="element-563">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-564">/session/</span>
        <span className="text-blue-600" data-id="element-565">{'{sessionId}'}</span>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-566">
        Configuration Options
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-567">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-568">
          <thead data-id="element-569">
            <tr className="bg-gray-100" data-id="element-570">
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-571">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-572">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-573">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-574">
            <tr data-id="element-575">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-576">
                interaction.operation <span className="text-red-500" data-id="element-577">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-578">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-579">
                PURCHASE, AUTHORIZE, VERIFY, or NONE
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-580">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-581">
                interaction.returnUrl <span className="text-red-500" data-id="element-582">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-583">URL</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-584">
                Redirect URL after successful payment
              </td>
            </tr>
            <tr data-id="element-585">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-586">
                interaction.cancelUrl
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-587">URL</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-588">
                Redirect URL if payer cancels
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-589">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-590">
                interaction.merchant.name
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-591">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-592">
                Merchant name displayed on checkout page
              </td>
            </tr>
            <tr data-id="element-593">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-594">
                interaction.displayControl
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-595">Object</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-596">
                Control visibility of billing/shipping fields
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <InfoBox type="tip" title="Success Indicator" data-id="element-597">
        Use the successIndicator from the session creation response to verify
        the payment result when the payer returns to your site.
      </InfoBox>
    </DocPageLayout>;
}